import React from "react"
import PropTypes from "prop-types"
import { App, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"

import api from "@api"
import hasAccess from "@api/hasAccess"
import { useAppContext } from "@components/AppContext"
import { TransactionShape } from "@shapes"
import { TRANSACTION_STATUS } from "@components/Domain/Constants"

const retryErrorOperation = api.get("transactions/RetryErrorTransaction")
const retryFailedOperation = api.get("transactions/RetryFailedTransaction")

const operationsMap = {
  [TRANSACTION_STATUS.ERROR]: retryErrorOperation,
  [TRANSACTION_STATUS.FAILED]: retryFailedOperation,
}

const FUNDING_TYPE = 'FUNDING'

const LABEL_RETRY = "Retry"
const LABEL_TITLE = "Re-submit Transaction"
const LABEL_CONTENT = "Re-submits transaction for processing."
const LABEL_RETRY_MESSAGE = "Transaction submitted for processing"

const insufficientFundsTitle = "Retry Distribution"
const insufficientFundsContent = `By clicking 'Retry', Fundamental will attempt to
resend distributions to Dwolla for processing. Due to the error, funds have not
been debited from the distribution account. If the issue has been addressed,
funds will be resent to the investor bank account. In addition, investors will
be notified via email that distributions are on their way to their linked bank accounts.`

const closedAccountContent = `Before proceeding, ensure that you have connected a new
valid distributions bank account. Due to the error, funds have not been debited
from the original distributions account. By clicking 'Retry', Fundamental will
attempt to resend distributions to Dwolla for processing. If the issue has been
addressed, funds will be resent to the investor bank account. In addition,
investors will be notified via email that distributions are on their way to
their linked bank accounts.`

const noAccountContent = `By clicking Retry, Fundamental will attempt to resend
the transaction from the Dwolla balance to the Org's funding bank account for
processing. Funds have already been debited from the investor's account. If the
organization account information is correct, funds will be sent from the Dwolla
balance to the updated organization bank account.`


const dwollaCodeInfoMap = {
    'R01': {
      title: insufficientFundsTitle,
      content: insufficientFundsContent
    },
    'R09': {
      title: insufficientFundsTitle,
      content: insufficientFundsContent
    },
    'R02': {
      title: LABEL_TITLE,
      content: noAccountContent
    },
    'R03': {
      title: LABEL_TITLE,
      content: closedAccountContent
    }
}

const getModalInfo = (statusReason) => {
  const defaultInfo = { title: LABEL_TITLE, content: LABEL_CONTENT }

  if (!statusReason) {
    return defaultInfo
  }

  const { code } = JSON.parse(statusReason)

  return dwollaCodeInfoMap[code] ? dwollaCodeInfoMap[code] : defaultInfo
}

const TransactionRetry = ({ transaction, onAfterRetry }) => {
  const { modal } = App.useApp()

  const { request, showSuccessMessage } = useAppContext()

  const { id, type, status, isApproved, statusReason, retryTransactionId } = transaction

  const { title, content } = getModalInfo(statusReason)

  const isFunding = type === FUNDING_TYPE

  const isPending = status === TRANSACTION_STATUS.PENDING

  const retryOperation = operationsMap[status]

  const canRetry = hasAccess(['transactions-write'])

  const isRetried = !!retryTransactionId

  if (isFunding) {
    return null
  }

  if (isPending) {
    return null
  }

  if (!canRetry) {
    return null
  }

  if (!isApproved) {
    return null
  }

  if (!retryOperation) {
    return null
  }

  if (isRetried) {
    return null
  }

  const onClick = () => {
    modal.confirm({
      title,
      okText: LABEL_RETRY,
      content,
      onOk() {
        return request(retryOperation.id, { id })
          .then(({ data }) => onAfterRetry(data))
          .then(() => {
            showSuccessMessage(LABEL_RETRY_MESSAGE)
          })
      },
      onCancel() {}
    })
  }

  return (
    <Button
      icon={<ReloadOutlined />}
      type="text"
      size="small"
      onClick={onClick}
    >
      {LABEL_RETRY}
    </Button>
  )
}

TransactionRetry.propTypes = {
  transaction: TransactionShape.isRequired,
  onAfterRetry: PropTypes.func.isRequired,
}

export default TransactionRetry
